/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useState, useCallback } from 'react';
import i18n from '@/settings/i18n';
import { formatStoreNumber, parseStoreNumber } from '@/lib/intl-format';
import { useCollectionPageContext } from '../../hooks/useCollectionPageContext';
import { priceFormater } from '@/settings/index';
import MultiRangeSlider from './MultiRangeSlider';

const t = i18n.init('collections');

const clamp = (val: number, min: number, max: number) =>
  Math.min(Math.max(val, min), max);

const PriceFilter = () => {
  const { filters, onPriceRangeChange, selectedFilters } =
    useCollectionPageContext();

  const _minPrice = filters?.priceRange?.min
    ? filters.priceRange.min / 100
    : undefined;
  const _maxPrice = filters?.priceRange?.max
    ? filters.priceRange.max / 100
    : undefined;

  if (_minPrice == null || _maxPrice == null) {
    return null;
  }

  // canonical values from context
  const ctxMin = selectedFilters?.priceRange?.min
    ? selectedFilters.priceRange.min / 100
    : _minPrice;

  const ctxMax = selectedFilters?.priceRange?.max
    ? selectedFilters.priceRange.max / 100
    : _maxPrice;

  // committed values
  const [minPrice, setMinPrice] = useState(ctxMin);
  const [maxPrice, setMaxPrice] = useState(ctxMax);

  // drafts (live)
  const [draftMinPrice, setDraftMinPrice] = useState(ctxMin);
  const [draftMaxPrice, setDraftMaxPrice] = useState(ctxMax);

  // raw input text while typing, so a trailing decimal separator ("12,")
  // isn't reformatted away before the next digit lands
  const [minText, setMinText] = useState<string | null>(null);
  const [maxText, setMaxText] = useState<string | null>(null);

  // sync from context
  useEffect(() => {
    setMinPrice(ctxMin);
    setMaxPrice(ctxMax);
    setDraftMinPrice(ctxMin);
    setDraftMaxPrice(ctxMax);
  }, [ctxMin, ctxMax]);

  const commit = useCallback(
    (min: number, max: number) => {
      const safeMin = clamp(min, _minPrice, _maxPrice);
      const safeMax = clamp(max, safeMin, _maxPrice);

      setMinPrice(safeMin);
      setMaxPrice(safeMax);
      setDraftMinPrice(safeMin);
      setDraftMaxPrice(safeMax);
      setMinText(null);
      setMaxText(null);

      onPriceRangeChange(Math.round(safeMin * 100), Math.round(safeMax * 100)); // back to cents
    },
    [_minPrice, _maxPrice, onPriceRangeChange],
  );

  const currencySymbol = priceFormater.getCurrencySymbol();
  const symbolTrailing = priceFormater.isCurrencySymbolTrailing();

  return (
    <div className="p-4">
      <h3 className="text-sm font-bold">{t`PRICE RANGE`}</h3>
      {/* Number inputs */}
      <div className="mt-2 grid grid-cols-2 gap-x-2">
        <label className="flex items-center gap-0.5 rounded border border-gray-500 px-2">
          {!symbolTrailing && <span translate="no">{currencySymbol}</span>}
          <input
            type="text"
            className="h-10 w-full focus:outline-none"
            value={
              minText ??
              (Number.isNaN(draftMinPrice)
                ? ''
                : formatStoreNumber(Math.round(draftMinPrice)))
            }
            onChange={(e) => {
              // Drop the thousands separators before converting to number
              const value = parseStoreNumber(e.target.value);

              if (!Number.isNaN(value)) {
                setMinText(e.target.value);
                setDraftMinPrice(value);
              }
            }}
            onBlur={() => commit(draftMinPrice, draftMaxPrice)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commit(draftMinPrice, draftMaxPrice);
              }

              if (e.key === 'Escape') {
                setDraftMinPrice(minPrice);
                setDraftMaxPrice(maxPrice);
                setMinText(null);
                setMaxText(null);
              }
            }}
          />
          {symbolTrailing && <span translate="no">{currencySymbol}</span>}
        </label>

        <label className="flex items-center gap-0.5 rounded border px-2">
          {!symbolTrailing && <span translate="no">{currencySymbol}</span>}
          <input
            type="text"
            className="h-10 w-full focus:outline-none"
            value={
              maxText ??
              (Number.isNaN(draftMaxPrice)
                ? ''
                : formatStoreNumber(Math.round(draftMaxPrice)))
            }
            onChange={(e) => {
              // Drop the thousands separators before converting to number
              const value = parseStoreNumber(e.target.value);

              if (!Number.isNaN(value)) {
                setMaxText(e.target.value);
                setDraftMaxPrice(value);
              }
            }}
            onBlur={() => commit(draftMinPrice, draftMaxPrice)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commit(draftMinPrice, draftMaxPrice);
              }

              if (e.key === 'Escape') {
                setDraftMinPrice(minPrice);
                setDraftMaxPrice(maxPrice);
                setMinText(null);
                setMaxText(null);
              }
            }}
          />
          {symbolTrailing && <span translate="no">{currencySymbol}</span>}
        </label>
      </div>
      {/* Slider */}
      <MultiRangeSlider
        min={_minPrice}
        max={_maxPrice}
        valueMin={draftMinPrice}
        valueMax={draftMaxPrice}
        onChange={({ min, max }) => {
          setDraftMinPrice(min);
          setDraftMaxPrice(max);
          setMinText(null);
          setMaxText(null);
        }}
        onCommit={({ min, max }) => {
          commit(min, max);
        }}
      />
    </div>
  );
};

export default PriceFilter;
